import { useRef, useState } from "react"
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { ApplicationIcon } from "./application-icon"
import { useApplicationSwitcherSync } from "./useApplicationSwitcherSync"
import { isEnabled } from "lib/@components/feature"
import { registerFeatureWithDescription } from "lib/features"
import { getReference, setReference } from "lib/@hooks/use-reference"

export const FEATURE_FLAG_APPLICATION_SWITCHER = registerFeatureWithDescription({
    numericalId: 400,
    id: "applicationSwitcher-all-users",
    name: "Application Switcher",
    readyForProduction: true,
    descriptionLines: ["Enables Application Switcher"],
})

export function ApplicationSwitcher() {
    const anchorRef = useRef(null)
    const [open, setOpen] = useState(false)
    const { showHelp } = getReference({ showHelp: false })

    const { options, selected, shouldShow, selectApp } = useApplicationSwitcherSync()
    if (!shouldShow) return null
    if (!isEnabled(FEATURE_FLAG_APPLICATION_SWITCHER)) return null
    return (
        <Box ref={anchorRef} display="inline-flex" mb={2}>
            <Tooltip title="Applications" arrow>
                <Button
                    onClick={() => setOpen((v) => !v)}
                    variant="text"
                    aria-haspopup="menu"
                    aria-expanded={open ? "true" : "false"}
                    data-cy="application-switcher-trigger"
                    sx={{
                        px: 1,
                        py: 0.5,
                        minHeight: 36,
                        minWidth: 0,
                        color: "primary.main",
                        display: "inline-flex",
                        alignItems: "center",
                        "&:hover": { backgroundColor: "action.hover" },
                    }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            lineHeight: (t) => t.typography.body1.lineHeight,
                            "& svg": { verticalAlign: "middle" },
                        }}
                    >
                        <ApplicationIcon appId={selected?.id} icon={selected?.icon} width={16} height={16} />
                        <Typography variant="caption" noWrap>
                            {selected?.name}
                        </Typography>
                        <Box
                            sx={{
                                fontSize: "1rem",
                                lineHeight: (t) => t.typography.caption.lineHeight,
                                transition: "transform 0.3s linear",
                                transform: `rotate(${open ? -180 : 0}deg)`,
                                display: "inline-flex",
                                alignItems: "center",
                            }}
                        >
                            <Iconify icon="mdi:chevron-down" />
                        </Box>
                    </Box>
                </Button>
            </Tooltip>

            <Menu
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { borderRadius: 1.5, minWidth: 200, p: 0.5 } } }}
                MenuListProps={{ "aria-label": "Application actions", dense: true }}
            >
                {options.map((opt) => {
                    const isSelected = opt.id === selected?.id
                    return (
                        <MenuItem
                            key={opt.id}
                            id={`app-${opt.id}`}
                            data-cy={`application-menu-item-${opt.id}`}
                            selected={isSelected}
                            onClick={() => {
                                selectApp(opt.id)
                                setOpen(false)
                                if (showHelp && !isSelected) setReference({ showHelp: false }, "help")
                            }}
                            sx={{ minHeight: 36, px: 1.25, borderRadius: 1, gap: 1 }}
                        >
                            <ListItemIcon sx={{ minWidth: 28, display: "inline-flex", alignItems: "center" }}>
                                <ApplicationIcon appId={opt.id} icon={opt.icon} width={24} height={24} />
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{ variant: "body1", noWrap: true }}
                                primary={opt.name}
                            />
                            {isSelected && (
                                <Box ml="auto" display="inline-flex">
                                    <Iconify icon="mdi:check" width={18} height={18} aria-label="Selected" />
                                </Box>
                            )}
                        </MenuItem>
                    )
                })}
            </Menu>
        </Box>
    )
}
